/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/jsx-filename-extension */
import React, { Component } from 'react';
import PropTypes from 'prop-types';

import Fade from 'react-reveal/Fade';
import Header from 'parts/Header';
import Discuss from 'parts/Discuss';
import Footer from 'parts/Footer';
import Button from 'elements/Button';

// Service data mapping
const serviceData = {
  'mobile-development': {
    title: 'Mobile Development',
    description: 'We boast of our mobile applications. We build eye-catchy, highly efficient mobile apps that are easy to use. From business apps to consumer apps to entertainment apps, we have built it all.',
    detailedDescription: 'In today\'s mobile-first world, having a robust mobile application is crucial for business success. ERapid Technologies specializes in creating high-performance, user-friendly mobile applications that drive engagement and deliver exceptional user experiences. Our team of expert mobile developers leverages cutting-edge technologies and industry best practices to build apps that stand out in the competitive app marketplace.',
    features: [
      'Native iOS and Android Development',
      'Cross-platform Mobile App Development (React Native, Flutter)',
      'Mobile UI/UX Design & Prototyping',
      'App Testing & Quality Assurance',
      'App Store Optimization (ASO)',
      'Mobile App Maintenance & Support',
      'API Integration & Backend Services',
      'Push Notifications & Real-time Features',
      'Mobile Analytics & Performance Monitoring',
      'Enterprise Mobile Solutions',
    ],
    benefits: [
      'Increased customer engagement and retention',
      'Enhanced brand visibility and market reach',
      'Improved operational efficiency',
      'Real-time data access and synchronization',
      'Offline functionality and data caching',
      'Seamless integration with existing systems',
    ],
    technologies: ['React Native', 'Flutter', 'Swift', 'Kotlin', 'Java', 'Objective-C', 'Xamarin', 'Ionic'],
    process: [
      { step: 'Discovery & Planning', description: 'We analyze your requirements, target audience, and business goals to create a comprehensive mobile app strategy.' },
      { step: 'Design & Prototyping', description: 'Our designers create intuitive, user-friendly interfaces with wireframes and interactive prototypes.' },
      { step: 'Development & Integration', description: 'Our developers build your app using the latest technologies, ensuring seamless integration with backend systems.' },
      { step: 'Testing & Quality Assurance', description: 'Rigorous testing across multiple devices and platforms to ensure flawless performance.' },
      { step: 'Deployment & Launch', description: 'We handle app store submissions and ensure a smooth launch process.' },
      { step: 'Maintenance & Updates', description: 'Ongoing support, bug fixes, and feature updates to keep your app current and competitive.' },
    ],
  },
  'web-development': {
    title: 'Web Development Services',
    description: 'We are proficient in developing simple, technical, and complex web applications. We build aesthetically pleasing and resilient websites across all frameworks using JavaScript, React JS, Vue JS, Angular JS and many more.',
    detailedDescription: 'ERapid Technologies delivers world-class web development services that transform your digital presence. We create scalable, secure, and high-performing web applications tailored to your business needs. Our expertise spans from simple responsive websites to complex enterprise-level web applications, ensuring your digital solutions drive business growth and enhance user engagement.',
    features: [
      'Frontend Development (React, Vue, Angular)',
      'Backend Development (Node.js, Python, Java)',
      'Full-Stack Web Applications',
      'Progressive Web Apps (PWA)',
      'E-commerce Solutions & Online Stores',
      'Custom Web Application Development',
      'RESTful & GraphQL API Development',
      'Content Management Systems (CMS)',
      'Single Page Applications (SPA)',
      'Responsive & Mobile-First Design',
      'Web Performance Optimization',
      'SEO-Friendly Web Development',
    ],
    benefits: [
      'Scalable architecture that grows with your business',
      'Enhanced user experience and engagement',
      'Improved search engine visibility',
      'Cross-platform compatibility',
      'Secure and reliable web solutions',
      'Reduced operational costs through automation',
    ],
    technologies: ['React.js', 'Vue.js', 'Angular.js', 'Node.js', 'Python', 'Django', 'Flask', 'Express.js', 'Next.js', 'Nuxt.js'],
    process: [
      { step: 'Requirements Analysis', description: 'Understanding your business objectives, target audience, and technical requirements.' },
      { step: 'Architecture Design', description: 'Designing scalable and maintainable system architecture.' },
      { step: 'UI/UX Design', description: 'Creating intuitive and visually appealing user interfaces.' },
      { step: 'Development', description: 'Building robust web applications using modern frameworks and best practices.' },
      { step: 'Testing & QA', description: 'Comprehensive testing to ensure functionality, performance, and security.' },
      { step: 'Deployment & Launch', description: 'Seamless deployment to production with minimal downtime.' },
      { step: 'Maintenance & Support', description: 'Ongoing maintenance, updates, and technical support.' },
    ],
  },
  'ui-ux-design': {
    title: 'UI/UX Design',
    description: 'Our highly efficient UI/UX designers craft beautiful applications that promise astounding user experience. We consistently brain-storm on building elite designs that are functionally simple to use.',
    detailedDescription: 'Great design is at the heart of exceptional user experiences. ERapid Technologies\' UI/UX design team combines creativity with user-centered design principles to create interfaces that are not only visually stunning but also highly functional and intuitive. We focus on understanding user behavior, preferences, and pain points to design solutions that delight users and drive business success.',
    features: [
      'User Research & Analysis',
      'Wireframing & Prototyping',
      'Visual Design & Branding',
      'User Experience Optimization',
      'Design System Creation',
      'Usability Testing & User Feedback',
      'Interaction Design & Micro-interactions',
      'Responsive & Adaptive Design',
      'Accessibility Design (WCAG Compliance)',
      'Design-to-Development Handoff',
      'Design Audit & Redesign Services',
      'Mobile & Web Interface Design',
    ],
    benefits: [
      'Improved user satisfaction and retention',
      'Reduced development time and costs',
      'Enhanced brand perception',
      'Higher conversion rates',
      'Better accessibility and inclusivity',
      'Competitive advantage through superior design',
    ],
    technologies: ['Figma', 'Sketch', 'Adobe XD', 'InVision', 'Zeplin', 'Principle', 'Framer'],
    process: [
      { step: 'User Research', description: 'Conducting user interviews, surveys, and market research to understand user needs.' },
      { step: 'Information Architecture', description: 'Structuring content and functionality for optimal user navigation.' },
      { step: 'Wireframing', description: 'Creating low-fidelity layouts to establish structure and hierarchy.' },
      { step: 'Prototyping', description: 'Building interactive prototypes to test and validate design concepts.' },
      { step: 'Visual Design', description: 'Applying colors, typography, and visual elements to create compelling designs.' },
      { step: 'Usability Testing', description: 'Testing designs with real users to identify and fix usability issues.' },
      { step: 'Design Handoff', description: 'Preparing design specifications and assets for development.' },
    ],
  },
  'product-engineering': {
    title: 'Product Engineering',
    description: 'We specialize in the art of making innovative break-through products. We build systems and processes that are geared for agility. If you have an idea, we have a solution.',
    detailedDescription: 'Product engineering is more than just development—it\'s about creating products that solve real problems and deliver value to users. ERapid Technologies takes a holistic approach to product engineering, combining technical expertise with business acumen to build products that are not only technically sound but also market-ready and scalable. From concept to launch and beyond, we guide you through every stage of product development.',
    features: [
      'Product Strategy & Planning',
      'MVP Development & Rapid Prototyping',
      'Agile Product Development',
      'Product Architecture Design',
      'Scalable System Development',
      'Product Lifecycle Management',
      'Feature Prioritization & Roadmap Planning',
      'Product-Market Fit Analysis',
      'Technical Due Diligence',
      'Product Performance Optimization',
      'Cross-functional Team Collaboration',
      'Go-to-Market Strategy Support',
    ],
    benefits: [
      'Faster time-to-market with MVP approach',
      'Reduced development risks through iterative development',
      'Scalable products that grow with your business',
      'Data-driven product decisions',
      'Improved product quality and reliability',
      'Better alignment between business and technology',
    ],
    technologies: ['Agile/Scrum', 'Lean Startup', 'Design Thinking', 'DevOps', 'CI/CD', 'Microservices'],
    process: [
      { step: 'Ideation & Validation', description: 'Validating your product idea through market research and user feedback.' },
      { step: 'Product Strategy', description: 'Defining product vision, goals, and roadmap aligned with business objectives.' },
      { step: 'MVP Development', description: 'Building a minimum viable product to test core assumptions quickly.' },
      { step: 'Iterative Development', description: 'Continuously improving the product based on user feedback and data.' },
      { step: 'Scaling & Optimization', description: 'Optimizing performance and scaling infrastructure to support growth.' },
      { step: 'Product Evolution', description: 'Ongoing enhancement and feature development to stay competitive.' },
    ],
  },
  'cloud-managed-services': {
    title: 'Cloud Managed Services',
    description: 'We provide efficient product solutions that are hosted and managed with various cloud service providers. It helps in load balancing offering reliable and efficient tracking system to monitor load in real time.',
    detailedDescription: 'Cloud infrastructure management is critical for modern businesses. ERapid Technologies offers comprehensive cloud managed services that ensure your applications run smoothly, securely, and cost-effectively in the cloud. We handle everything from infrastructure setup to ongoing monitoring and optimization, allowing you to focus on your core business while we manage your cloud infrastructure.',
    features: [
      'Cloud Infrastructure Setup & Configuration',
      'AWS, Azure, GCP Management',
      'Cloud Migration Services',
      'Load Balancing & Auto-scaling',
      'Cloud Security & Compliance',
      '24/7 Cloud Monitoring & Support',
      'Cost Optimization & Resource Management',
      'Disaster Recovery & Backup Solutions',
      'Container Orchestration (Kubernetes, Docker)',
      'Serverless Architecture Implementation',
      'Multi-cloud & Hybrid Cloud Solutions',
      'Cloud Performance Optimization',
    ],
    benefits: [
      'Reduced infrastructure costs',
      'Improved scalability and flexibility',
      'Enhanced security and compliance',
      '24/7 monitoring and proactive support',
      'Faster deployment and time-to-market',
      'Disaster recovery and business continuity',
    ],
    technologies: ['AWS', 'Azure', 'Google Cloud', 'Kubernetes', 'Docker', 'Terraform', 'Ansible', 'Jenkins'],
    process: [
      { step: 'Cloud Assessment', description: 'Evaluating your current infrastructure and identifying cloud migration opportunities.' },
      { step: 'Architecture Design', description: 'Designing cloud architecture optimized for performance, security, and cost.' },
      { step: 'Migration Planning', description: 'Creating detailed migration plans with minimal downtime strategies.' },
      { step: 'Implementation', description: 'Setting up cloud infrastructure and migrating applications and data.' },
      { step: 'Optimization', description: 'Continuously optimizing cloud resources for performance and cost efficiency.' },
      { step: 'Managed Services', description: 'Providing ongoing monitoring, maintenance, and support services.' },
    ],
  },
  'technology-migration': {
    title: 'Technology Migration',
    description: 'Facing troubles scaling your application? We will help you migrate your app to the latest technology with scalable architecture. We do in-depth analysis of your existing product, make assessments, and build a scalable, high-tolerance application.',
    detailedDescription: 'Technology migration is a critical process that requires careful planning and execution. ERapid Technologies specializes in helping businesses modernize their technology stack, migrate to cloud platforms, and upgrade legacy systems. Our proven migration methodology ensures minimal disruption to your business operations while delivering improved performance, scalability, and maintainability.',
    features: [
      'Legacy System Modernization',
      'Technology Stack Migration',
      'Database Migration & Optimization',
      'Cloud Migration Services',
      'Code Refactoring & Modernization',
      'Zero-Downtime Migration',
      'Data Migration & Validation',
      'API Migration & Integration',
      'Platform Migration (e.g., .NET to Node.js)',
      'Microservices Migration',
      'Migration Testing & Validation',
      'Post-Migration Support & Optimization',
    ],
    benefits: [
      'Improved system performance and scalability',
      'Reduced maintenance costs',
      'Enhanced security and compliance',
      'Better developer productivity',
      'Access to modern features and capabilities',
      'Future-proof technology stack',
    ],
    technologies: ['Docker', 'Kubernetes', 'CI/CD Tools', 'Migration Tools', 'Monitoring Tools'],
    process: [
      { step: 'Current State Assessment', description: 'Analyzing existing systems, architecture, and identifying migration challenges.' },
      { step: 'Migration Strategy', description: 'Developing comprehensive migration plan with risk mitigation strategies.' },
      { step: 'Proof of Concept', description: 'Building and testing migration approach on a small scale.' },
      { step: 'Data Migration', description: 'Safely migrating data with validation and integrity checks.' },
      { step: 'Application Migration', description: 'Migrating applications with minimal downtime using proven techniques.' },
      { step: 'Testing & Validation', description: 'Thorough testing to ensure functionality and performance meet requirements.' },
      { step: 'Go-Live & Support', description: 'Executing migration and providing immediate post-migration support.' },
    ],
  },
  'internet-of-things': {
    title: 'Internet Of Things',
    description: 'We leverage the power of technology to turn simple everyday objects into a smart object. You can IoT-ize your assets to increase productivity and make it more efficient.',
    detailedDescription: 'The Internet of Things (IoT) is revolutionizing how businesses operate and interact with their assets. ERapid Technologies helps businesses harness the power of IoT to create smart, connected solutions that drive efficiency, reduce costs, and enable data-driven decision-making. From sensor integration to cloud platforms and analytics, we provide end-to-end IoT solutions.',
    features: [
      'IoT Device Development & Firmware',
      'IoT Platform Integration',
      'Sensor Integration & Data Collection',
      'IoT Analytics & Insights',
      'Smart Device Connectivity (WiFi, Bluetooth, LoRa)',
      'IoT Security Solutions',
      'Edge Computing & Real-time Processing',
      'IoT Dashboard & Visualization',
      'Predictive Maintenance Solutions',
      'Industrial IoT (IIoT) Applications',
      'IoT Cloud Platform Development',
      'Device Management & Remote Monitoring',
    ],
    benefits: [
      'Real-time monitoring and control',
      'Improved operational efficiency',
      'Predictive maintenance capabilities',
      'Data-driven insights and analytics',
      'Reduced operational costs',
      'Enhanced customer experiences',
    ],
    technologies: ['MQTT', 'CoAP', 'AWS IoT', 'Azure IoT', 'Google Cloud IoT', 'Raspberry Pi', 'Arduino', 'ESP32'],
    process: [
      { step: 'IoT Strategy & Planning', description: 'Defining IoT objectives, use cases, and technical requirements.' },
      { step: 'Hardware Selection', description: 'Choosing appropriate sensors, devices, and connectivity solutions.' },
      { step: 'Firmware Development', description: 'Developing embedded software for IoT devices.' },
      { step: 'Cloud Platform Setup', description: 'Setting up IoT cloud platforms for data collection and processing.' },
      { step: 'Integration & Connectivity', description: 'Connecting devices and ensuring reliable data transmission.' },
      { step: 'Analytics & Dashboards', description: 'Building analytics solutions and visualization dashboards.' },
      { step: 'Deployment & Monitoring', description: 'Deploying IoT solutions and setting up monitoring systems.' },
    ],
  },
  'deployment-support': {
    title: 'Deployment & Support',
    description: 'Burnt out from your past experience? No worries. We will help you enhance and deploy your product. We offer bug resolution, testing, efficiency check, peer review, and complete support to ensure that your product up and running in no time.',
    detailedDescription: 'Successful deployment and ongoing support are crucial for maintaining a reliable and high-performing application. ERapid Technologies provides comprehensive deployment and support services that ensure your applications are deployed smoothly, run efficiently, and receive timely updates and fixes. Our team handles everything from CI/CD pipeline setup to 24/7 monitoring and support.',
    features: [
      'CI/CD Pipeline Setup & Automation',
      'Automated Deployment & Rollback',
      'Production Environment Setup',
      'Bug Resolution & Testing',
      'Performance Optimization',
      'Post-Deployment Support',
      'DevOps Implementation',
      'Infrastructure as Code (IaC)',
      'Container Orchestration',
      'Monitoring & Alerting Setup',
      'Security Hardening',
      'Documentation & Runbooks',
    ],
    benefits: [
      'Faster and more reliable deployments',
      'Reduced deployment risks',
      'Improved application stability',
      'Faster issue resolution',
      'Proactive monitoring and alerting',
      'Reduced operational overhead',
    ],
    technologies: ['Jenkins', 'GitLab CI', 'GitHub Actions', 'Docker', 'Kubernetes', 'Terraform', 'Ansible', 'Prometheus', 'Grafana'],
    process: [
      { step: 'Environment Setup', description: 'Configuring development, staging, and production environments.' },
      { step: 'CI/CD Pipeline Development', description: 'Building automated build, test, and deployment pipelines.' },
      { step: 'Deployment Strategy', description: 'Planning deployment approach (blue-green, canary, rolling updates).' },
      { step: 'Pre-Deployment Testing', description: 'Comprehensive testing before production deployment.' },
      { step: 'Deployment Execution', description: 'Executing deployment with monitoring and rollback capabilities.' },
      { step: 'Post-Deployment Validation', description: 'Verifying deployment success and application functionality.' },
      { step: 'Ongoing Support', description: 'Providing continuous monitoring, maintenance, and support.' },
    ],
  },
  'maintenance-scaling': {
    title: 'Maintenance & Scaling',
    description: 'We offer maintenance and scaling service where we keep a constant check on performance. To do so, we do crash test, API failure check, Server failure check, bug resolution, addition of new features, agility, and performance enhancement.',
    detailedDescription: 'Maintaining and scaling applications is essential for long-term success. ERapid Technologies provides comprehensive maintenance and scaling services that keep your applications running smoothly, performing optimally, and ready to handle growth. We proactively monitor, optimize, and enhance your systems to ensure they continue to meet your business needs as you scale.',
    features: [
      'Application Performance Monitoring',
      'Regular System Health Checks',
      'Bug Fixes & Updates',
      'Feature Enhancements',
      'Scalability Planning & Implementation',
      'Performance Optimization',
      'Load Testing & Capacity Planning',
      'Database Optimization',
      'Code Refactoring & Technical Debt Reduction',
      'Security Updates & Patches',
      'Backup & Disaster Recovery',
      '24/7 Monitoring & Incident Response',
    ],
    benefits: [
      'Improved application reliability',
      'Better performance and user experience',
      'Cost-effective scaling strategies',
      'Reduced downtime and outages',
      'Proactive issue detection and resolution',
      'Extended application lifespan',
    ],
    technologies: ['APM Tools', 'Load Testing Tools', 'Monitoring Tools', 'Auto-scaling Solutions'],
    process: [
      { step: 'Performance Baseline', description: 'Establishing current performance metrics and benchmarks.' },
      { step: 'Monitoring Setup', description: 'Implementing comprehensive monitoring and alerting systems.' },
      { step: 'Regular Maintenance', description: 'Scheduled maintenance, updates, and optimizations.' },
      { step: 'Performance Analysis', description: 'Analyzing performance data to identify bottlenecks and issues.' },
      { step: 'Optimization', description: 'Implementing optimizations to improve performance and efficiency.' },
      { step: 'Scaling Strategy', description: 'Planning and implementing scaling solutions for growth.' },
      { step: 'Continuous Improvement', description: 'Ongoing monitoring and iterative improvements.' },
    ],
  },
  'data-engineering': {
    title: 'Data Engineering',
    description: 'If you want to leverage data to generate meaningful and actionable insights to help you take informed business decisions for your company, our data engineers can come in handy. We use the power of technology to analyze and learn the previous behavior of data, basis which we prepare it for analytical or operational uses.',
    detailedDescription: 'Data is the new oil, and effective data engineering is the refinery that transforms raw data into valuable insights. ERapid Technologies helps businesses build robust data infrastructure that enables data-driven decision-making. We design and implement data pipelines, data warehouses, and analytics solutions that turn your data into a competitive advantage.',
    features: [
      'Data Pipeline Development & Automation',
      'ETL/ELT Processes & Data Integration',
      'Data Warehouse Design & Implementation',
      'Big Data Solutions (Hadoop, Spark)',
      'Data Analytics & Reporting',
      'Real-time Data Processing & Streaming',
      'Data Lake Architecture',
      'Data Quality & Governance',
      'Business Intelligence Solutions',
      'Machine Learning Pipeline Integration',
      'Data Visualization & Dashboards',
      'Cloud Data Platform Implementation',
    ],
    benefits: [
      'Data-driven decision making',
      'Improved business intelligence',
      'Real-time insights and analytics',
      'Scalable data infrastructure',
      'Better data quality and reliability',
      'Cost-effective data processing',
    ],
    technologies: ['Apache Spark', 'Hadoop', 'Kafka', 'Airflow', 'Snowflake', 'Redshift', 'BigQuery', 'Tableau', 'Power BI'],
    process: [
      { step: 'Data Assessment', description: 'Evaluating data sources, quality, and business requirements.' },
      { step: 'Architecture Design', description: 'Designing scalable data architecture and pipelines.' },
      { step: 'Data Pipeline Development', description: 'Building ETL/ELT pipelines for data processing.' },
      { step: 'Data Warehouse Setup', description: 'Implementing data warehouses or data lakes for storage.' },
      { step: 'Analytics & Reporting', description: 'Building analytics solutions and reporting dashboards.' },
      { step: 'Optimization', description: 'Optimizing data pipelines for performance and cost efficiency.' },
      { step: 'Maintenance & Support', description: 'Ongoing monitoring, maintenance, and enhancement of data systems.' },
    ],
  },
  'architecture-design': {
    title: 'Architecture Design',
    description: 'Empower your project\'s success with our specialized software architecture design services. Our seasoned team crafts tailored, scalable, and high-performing solutions, optimizing every aspect of your software. Elevate your technology foundation with our expertise.',
    detailedDescription: 'Solid architecture is the foundation of every successful software system. ERapid Technologies provides expert software architecture design services that ensure your systems are scalable, maintainable, secure, and performant. Our architects work closely with your team to design solutions that align with your business goals and technical requirements.',
    features: [
      'System Architecture Design',
      'Microservices Architecture',
      'Cloud Architecture Planning',
      'Scalable System Design',
      'Security Architecture & Compliance',
      'Performance Optimization',
      'Event-Driven Architecture',
      'Serverless Architecture Design',
      'API Architecture & Design',
      'Database Architecture & Design',
      'Integration Architecture',
      'Technical Documentation & Specifications',
    ],
    benefits: [
      'Scalable and maintainable systems',
      'Reduced technical debt',
      'Improved system performance',
      'Enhanced security posture',
      'Faster development cycles',
      'Better alignment with business goals',
    ],
    technologies: ['Microservices', 'Event-Driven Architecture', 'API Gateway', 'Service Mesh', 'Message Queues'],
    process: [
      { step: 'Requirements Analysis', description: 'Understanding business requirements, constraints, and technical needs.' },
      { step: 'Architecture Planning', description: 'Designing high-level architecture and system components.' },
      { step: 'Detailed Design', description: 'Creating detailed architecture specifications and diagrams.' },
      { step: 'Technology Selection', description: 'Choosing appropriate technologies and frameworks.' },
      { step: 'Architecture Review', description: 'Reviewing architecture with stakeholders and technical teams.' },
      { step: 'Implementation Guidance', description: 'Providing guidance during implementation phase.' },
      { step: 'Architecture Evolution', description: 'Continuously evolving architecture as requirements change.' },
    ],
  },
};

export default class ServiceDetailPage extends Component {
  componentDidMount() {
    window.scrollTo(0, 0);
  }

  render() {
    const { match } = this.props;
    const serviceSlug = match?.params?.service || '';
    const service = serviceData[serviceSlug];

    if (!service) {
      return (
        <>
          <Header {...this.props} />
          <section className="pt-32 pb-24">
            <div className="container mx-auto px-4 text-center">
              <h1 className="text-5xl lg:text-6xl text-theme-blue font-bold mb-4">
                Service Not Found
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                The service you are looking for does not exist.
              </p>
              <Button
                href="/service"
                type="link"
                className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-theme-purple to-dark-theme-purple text-white rounded-lg shadow-lg hover:shadow-xl transform transition-all duration-300 hover:scale-105"
              >
                View All Services
              </Button>
            </div>
          </section>
          <Footer />
        </>
      );
    }

    return (
      <>
        <Header {...this.props} />
        <section className="relative pt-32 pb-20 bg-gradient-to-b from-white to-gray-50 overflow-hidden">
          {/* Decorative background elements */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-theme-purple/5 rounded-full blur-3xl transform translate-x-1/4 -translate-y-1/4" />
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-dark-theme-purple/5 rounded-full blur-3xl transform -translate-x-1/4 translate-y-1/4" />

          <div className="container mx-auto px-4 relative z-10">
            <Fade bottom>
              <div className="text-center mb-12">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-theme-purple/10 to-dark-theme-purple/10 rounded-2xl mb-6">
                  <svg className="w-10 h-10 text-theme-purple" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h1 className="text-5xl lg:text-6xl text-theme-blue font-bold mb-6">
                  {service.title}
                </h1>
                <p className="font-light text-lg lg:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-4">
                  {service.description}
                </p>
                {service.detailedDescription && (
                  <p className="font-light text-base lg:text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed mb-8">
                    {service.detailedDescription}
                  </p>
                )}
                <Button
                  href="/discuss-project"
                  type="link"
                  className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-theme-purple to-dark-theme-purple text-white rounded-lg shadow-lg hover:shadow-xl transform transition-all duration-300 hover:scale-105"
                >
                  Get Quote Now
                  <svg className="ml-2 w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Button>
              </div>
            </Fade>
          </div>
        </section>

        <section className="py-16 bg-white relative overflow-hidden">
          {/* Decorative background elements */}
          <div className="absolute top-0 left-0 w-64 h-64 bg-theme-purple/3 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-dark-theme-purple/3 rounded-full blur-3xl" />

          <div className="container mx-auto px-4 relative z-10">
            <Fade bottom>
              <div className="text-center mb-12">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-theme-purple/10 to-dark-theme-purple/10 rounded-xl mb-4">
                  <svg className="w-8 h-8 text-theme-purple" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <h2 className="text-3xl lg:text-4xl text-theme-blue text-center font-bold mb-4">
                  What We Offer
                </h2>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  Comprehensive services tailored to your business needs
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {service.features.map((feature, index) => (
                  <Fade bottom delay={200 * index} key={feature}>
                    <div className="group relative bg-gray-50 rounded-xl p-6 border border-gray-100 hover:shadow-lg hover:border-theme-purple/30 transition-all duration-300 hover:scale-105 overflow-hidden">
                      {/* Decorative gradient on hover */}
                      <div className="absolute inset-0 bg-gradient-to-br from-theme-purple/5 to-dark-theme-purple/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                      <div className="relative flex items-start z-10">
                        <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-theme-purple/10 to-dark-theme-purple/10 rounded-lg flex items-center justify-center mr-4 group-hover:from-theme-purple/20 group-hover:to-dark-theme-purple/20 transition-all duration-300">
                          <svg className="w-5 h-5 text-theme-purple" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <p className="text-gray-700 font-medium group-hover:text-theme-blue transition-colors duration-300">{feature}</p>
                      </div>
                    </div>
                  </Fade>
                ))}
              </div>
            </Fade>
          </div>
        </section>

        {service.benefits && service.benefits.length > 0 && (
          <section className="py-16 bg-gray-50">
            <div className="container mx-auto px-4">
              <Fade bottom>
                <h2 className="text-3xl lg:text-4xl text-theme-blue text-center font-bold mb-12">
                  Key Benefits
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                  {service.benefits.map((benefit, index) => (
                    <Fade bottom delay={200 * index} key={benefit}>
                      <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-md hover:shadow-lg transition-all duration-300">
                        <div className="flex items-start">
                          <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-theme-purple to-dark-theme-purple rounded-lg flex items-center justify-center mr-4">
                            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                          </div>
                          <p className="text-gray-700 font-medium leading-relaxed">{benefit}</p>
                        </div>
                      </div>
                    </Fade>
                  ))}
                </div>
              </Fade>
            </div>
          </section>
        )}

        {service.process && service.process.length > 0 && (
          <section className="py-16 bg-white">
            <div className="container mx-auto px-4">
              <Fade bottom>
                <h2 className="text-3xl lg:text-4xl text-theme-blue text-center font-bold mb-12">
                  Our Process
                </h2>
                <div className="max-w-5xl mx-auto">
                  <div className="space-y-8">
                    {service.process.map((step, index) => (
                      <Fade bottom delay={200 * index} key={step.step}>
                        <div className="flex items-start">
                          <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-theme-purple to-dark-theme-purple rounded-full flex items-center justify-center text-white font-bold text-lg mr-6">
                            {index + 1}
                          </div>
                          <div className="flex-grow">
                            <h3 className="text-xl font-bold text-theme-blue mb-2">{step.step}</h3>
                            <p className="text-gray-600 leading-relaxed">{step.description}</p>
                          </div>
                        </div>
                      </Fade>
                    ))}
                  </div>
                </div>
              </Fade>
            </div>
          </section>
        )}

        {service.technologies && service.technologies.length > 0 && (
          <section className="py-16 bg-gray-50">
            <div className="container mx-auto px-4">
              <Fade bottom>
                <h2 className="text-3xl lg:text-4xl text-theme-blue text-center font-bold mb-12">
                  Technologies We Use
                </h2>
                <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
                  {service.technologies.map((tech, index) => (
                    <Fade bottom delay={100 * index} key={tech}>
                      <span className="px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-lg border border-gray-200 hover:bg-theme-purple/10 hover:border-theme-purple/30 transition-colors duration-200">
                        {tech}
                      </span>
                    </Fade>
                  ))}
                </div>
              </Fade>
            </div>
          </section>
        )}

        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <Fade bottom>
              <div className="text-center">
                <h2 className="text-3xl lg:text-4xl text-theme-blue font-bold mb-4">
                  Ready to Get Started?
                </h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
                  Let&apos;s discuss how we can help transform your business with our
                  {' '}
                  {service.title}
                  {' '}
                  services.
                </p>
                <Button
                  href="/discuss-project"
                  type="link"
                  className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-theme-purple to-dark-theme-purple text-white rounded-lg shadow-lg hover:shadow-xl transform transition-all duration-300 hover:scale-105"
                >
                  Contact Us
                  <svg className="ml-2 w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Button>
              </div>
            </Fade>
          </div>
        </section>

        <Discuss {...this.props} />
        <Footer />
      </>
    );
  }
}

ServiceDetailPage.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      service: PropTypes.string,
    }),
  }),
};

ServiceDetailPage.defaultProps = {
  match: {
    params: {
      service: '',
    },
  },
};
